import type {
  InstitutionalHolderRecord,
  InstitutionalHoldersFileResponse,
} from '@/services/api';
import {
  formatInstitutionalHolderFullTitle,
  formatPositionTypeLabel,
  holderPositionKey,
} from '@/utils/institutionalHoldersChartData';

export const INSTITUTIONAL_HOLDER_HISTORY_QUARTERS = 4;

export interface InstitutionalHolderHistoryRow {
  quarterKey: string;
  quarterLabel: string;
  shares: number | null;
  isSoldOut: boolean;
  isHighlighted: boolean;
}

export interface InstitutionalHolderDetailPanel {
  /** Full title including position type, e.g. for aria-label. */
  investorName: string;
  institutionName: string;
  positionTypeLabel: string | null;
  cik: string;
  symbol: string;
  highlightedQuarterKey: string;
  /** Quarter used for ownership / value summary (usually the newest filing). */
  summaryQuarterKey: string;
  rows: InstitutionalHolderHistoryRow[];
  ownership?: number;
  marketValue?: number;
  firstAdded?: string;
}

function parseQuarterSortKey(key: string): number {
  const m = key.match(/^(\d{4})-Q([1-4])$/);
  if (!m) return 0;
  return parseInt(m[1]!, 10) * 100 + parseInt(m[2]!, 10);
}

function sortQuarterKeysDesc(keys: string[]): string[] {
  return [...keys].sort((a, b) => parseQuarterSortKey(b) - parseQuarterSortKey(a));
}

/** Clicked quarter plus up to three prior quarters (newest row first). */
function quarterHistoryWindow(
  sortedKeysDesc: string[],
  anchorQuarterKey: string,
  count: number
): string[] {
  const idx = sortedKeysDesc.indexOf(anchorQuarterKey);
  if (idx >= 0) {
    return sortedKeysDesc.slice(idx, idx + count);
  }
  return sortedKeysDesc.slice(0, count);
}

/** Newest quarter key where this position appears in the filing data. */
function latestQuarterKeyForPosition(
  sortedKeysDesc: string[],
  byQuarter: Map<string, Map<string, InstitutionalHolderRecord>>,
  positionKey: string
): string | null {
  for (const qk of sortedKeysDesc) {
    if (byQuarter.get(qk)?.has(positionKey)) return qk;
  }
  return null;
}

export function quarterKeyToLabel(quarterKey: string): string {
  const m = quarterKey.match(/^(\d{4})-Q([1-4])$/);
  return m ? `Q${m[2]} ${m[1]}` : quarterKey;
}

function buildPositionIndex(
  quarters: Record<string, { holders?: InstitutionalHolderRecord[] }>
): Map<string, Map<string, InstitutionalHolderRecord>> {
  const byQuarter = new Map<string, Map<string, InstitutionalHolderRecord>>();
  for (const [qk, bucket] of Object.entries(quarters)) {
    const m = new Map<string, InstitutionalHolderRecord>();
    for (const h of bucket.holders ?? []) {
      m.set(holderPositionKey(h), h);
    }
    byQuarter.set(qk, m);
  }
  return byQuarter;
}

/**
 * Four most recent filing quarters (newest first), with the clicked quarter bolded.
 * Summary stats use the holder's newest quarter on file so Q1 2026 appears even when
 * the user opened the panel from an older chart column (e.g. Q4 2025).
 */
export function buildInstitutionalHolderDetailPanel(
  payload: InstitutionalHoldersFileResponse | null | undefined,
  stockSymbol: string,
  positionKey: string,
  highlightedQuarterKey: string
): InstitutionalHolderDetailPanel | null {
  const quarters = payload?.quarters;
  if (!quarters || !positionKey) return null;

  const sortedKeys = sortQuarterKeysDesc(
    Object.keys(quarters).filter((k) => (quarters[k]?.holders?.length ?? 0) > 0)
  );
  if (sortedKeys.length === 0) return null;

  const byQuarter = buildPositionIndex(quarters);

  const latestHolderQuarter = latestQuarterKeyForPosition(sortedKeys, byQuarter, positionKey);
  const summaryQuarterKey = latestHolderQuarter ?? highlightedQuarterKey;

  const recentKeys = sortedKeys.slice(0, INSTITUTIONAL_HOLDER_HISTORY_QUARTERS);
  const anchorInWindow = recentKeys.includes(highlightedQuarterKey);
  const displayKeys = anchorInWindow
    ? recentKeys
    : quarterHistoryWindow(sortedKeys, highlightedQuarterKey, INSTITUTIONAL_HOLDER_HISTORY_QUARTERS);

  let profile: InstitutionalHolderRecord | undefined =
    byQuarter.get(summaryQuarterKey)?.get(positionKey);
  if (!profile) {
    for (const qk of displayKeys) {
      profile = byQuarter.get(qk)?.get(positionKey);
      if (profile) break;
    }
  }

  const institutionName =
    (profile?.investorName ?? '').trim() ||
    (profile?.cik ? `CIK ${profile.cik.replace(/^0+/, '')}` : 'Unknown institution');
  const positionTypeLabel = profile ? formatPositionTypeLabel(profile.putCallShare) : null;
  const investorName = profile
    ? formatInstitutionalHolderFullTitle(profile)
    : institutionName;

  const rows: InstitutionalHolderHistoryRow[] = displayKeys.map((qk) => {
    const h = byQuarter.get(qk)?.get(positionKey);
    const shares = h?.sharesNumber;
    return {
      quarterKey: qk,
      quarterLabel: quarterKeyToLabel(qk),
      shares: h == null ? null : (shares ?? 0),
      isSoldOut: Boolean(h?.isSoldOut),
      isHighlighted: qk === highlightedQuarterKey,
    };
  });

  return {
    investorName,
    institutionName,
    positionTypeLabel,
    cik: profile?.cik?.trim() ?? '',
    symbol: stockSymbol.toUpperCase(),
    highlightedQuarterKey,
    summaryQuarterKey,
    rows,
    ownership: profile?.ownership,
    marketValue: profile?.marketValue,
    firstAdded: profile?.firstAdded,
  };
}

export function formatHolderHistoryShares(
  shares: number | null,
  isSoldOut: boolean
): string {
  if (shares == null) return 'Not held';
  if (isSoldOut && shares <= 0) return 'Sold out';
  return `${shares.toLocaleString()} shares`;
}
